import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { tokens } from '../theme';

const CustomTable = ({
  columns = [],
  rows = [],
  rowKey = 'id',
  actions = [],
  rowsPerPageOptions = [5, 10, 25],
  emptyMessage = 'Nenhum registro encontrado.',
  elevation = 1,
  onRowClick,
  hidePagination = false,
  containerSx = {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  // When pagination is hidden, display all rows
  const paginated = hidePagination
    ? rows
    : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={elevation}
        sx={{ borderRadius: 2, overflowX: 'auto', ...containerSx }}
      >
        <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
          {/* ── Cabeçalho ── */}
          <TableHead sx={{ bgcolor: '#FAFAFA' }}>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    color: '#2C2C2C',
                    fontWeight: 600,
                    fontSize: '14px',
                    width: col.width ?? 'auto',
                  }}
                >
                  {col.headerName}
                </TableCell>
              ))}

              {/* Coluna de ações (sem cabeçalho visível) */}
              {actions.length > 0 && <TableCell sx={{ width: actions.length * 44 }} />}
            </TableRow>
          </TableHead>

          {/* ── Corpo ── */}
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (actions.length > 0 ? 1 : 0)} align="center">
                  <Typography variant="body2" sx={{ color: '#737373', py: 2 }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row) => (
                <TableRow
                  key={row[rowKey]}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    '&:hover': { bgcolor: '#FAFAFA' },
                    cursor: onRowClick ? 'pointer' : 'default',
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.field} sx={{ color: '#737373', fontSize: '14px' }}>
                      {col.render ? col.render(row[col.field], row) : (row[col.field] ?? '—')}
                    </TableCell>
                  ))}

                  {/* Célula de ações */}
                  {actions.length > 0 && (
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {actions.map((action, idx) => (
                          <IconButton
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              action.onClick(row);
                            }}
                            aria-label={action.tooltip ?? `action-${idx}`}
                            sx={{
                              bgcolor: '#fafafa',
                              width: 36,
                              height: 36,
                              '&:hover': {
                                bgcolor: action.color ?? colors.greenAccent[600],
                              },
                            }}
                          >
                            {action.icon}
                          </IconButton>
                        ))}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Paginação ── */}
      {!hidePagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      )}
    </Box>
  );
};

export default CustomTable;
